import type { FC } from "react";
import React from "react";
import classNames from "classnames";
import css from "./LogoLoader.module.css";

type ArenkoIdentSvgProps = {
  className?: string;
  title?: string;
};

/**
 * Arenko identity mark SVG with 5 animated bars.
 * Styled via LogoLoader.module.css.
 */
const ArenkoIdentSvg: FC<ArenkoIdentSvgProps> = ({
  className,
  title = "Loading",
}) => {
  return (
    <svg
      className={classNames(css.svg, className)}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 34"
      focusable="false"
      role="img"
    >
      <title>{title}</title>

      {/* Ident */}
      <defs>
        <clipPath id="arenko-ident-clip">
          <path d="M18.979 0a2.67 2.67 0 0 1 2.68 2.68v27.873a2.67 2.67 0 0 1-2.68 2.681 2.67 2.67 0 0 1-2.681-2.68V2.68c0-1.447 1.234-2.681 2.68-2.681M2.68 11.532a2.67 2.67 0 0 1 2.682 2.68v4.937a2.67 2.67 0 0 1-2.681 2.68A2.67 2.67 0 0 1 0 19.15v-4.936a2.67 2.67 0 0 1 2.68-2.681M10.851 7.532a2.67 2.67 0 0 1 2.68 2.68v12.852a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.68-2.68V10.213c-.043-1.49 1.19-2.681 2.68-2.681M35.32 11.532a2.67 2.67 0 0 1 2.68 2.68v4.937a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.682-2.68v-4.936a2.67 2.67 0 0 1 2.681-2.681M27.149 7.532a2.67 2.67 0 0 1 2.68 2.68v12.852a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.68-2.68V10.213a2.67 2.67 0 0 1 2.68-2.681" />
        </clipPath>

        {/* Approximate clip regions for each ident bar (left -> right) */}
        <clipPath id="arenko-ident-bar-1">
          <rect x="-1" y="0" width="7" height="34" />
        </clipPath>
        <clipPath id="arenko-ident-bar-2">
          <rect x="7" y="0" width="7" height="34" />
        </clipPath>
        <clipPath id="arenko-ident-bar-3">
          <rect x="15" y="0" width="7" height="34" />
        </clipPath>
        <clipPath id="arenko-ident-bar-4">
          <rect x="23" y="0" width="7" height="34" />
        </clipPath>
        <clipPath id="arenko-ident-bar-5">
          <rect x="31" y="0" width="10" height="34" />
        </clipPath>
      </defs>

      <g className={css.identBase}>
        <path d="M18.979 0a2.67 2.67 0 0 1 2.68 2.68v27.873a2.67 2.67 0 0 1-2.68 2.681 2.67 2.67 0 0 1-2.681-2.68V2.68c0-1.447 1.234-2.681 2.68-2.681M2.68 11.532a2.67 2.67 0 0 1 2.682 2.68v4.937a2.67 2.67 0 0 1-2.681 2.68A2.67 2.67 0 0 1 0 19.15v-4.936a2.67 2.67 0 0 1 2.68-2.681M10.851 7.532a2.67 2.67 0 0 1 2.68 2.68v12.852a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.68-2.68V10.213c-.043-1.49 1.19-2.681 2.68-2.681M35.32 11.532a2.67 2.67 0 0 1 2.68 2.68v4.937a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.682-2.68v-4.936a2.67 2.67 0 0 1 2.681-2.681M27.149 7.532a2.67 2.67 0 0 1 2.68 2.68v12.852a2.67 2.67 0 0 1-2.68 2.68 2.67 2.67 0 0 1-2.68-2.68V10.213a2.67 2.67 0 0 1 2.68-2.681" />
      </g>

      {/* Animated fill per ident bar (no segments) */}
      <g clipPath="url(#arenko-ident-clip)" className={css.identFill}>
        <g clipPath="url(#arenko-ident-bar-1)">
          <rect
            className={classNames(css.fill, css.fill1)}
            x="-1"
            y="0"
            width="7"
            height="34"
          />
        </g>
        <g clipPath="url(#arenko-ident-bar-2)">
          <rect
            className={classNames(css.fill, css.fill2)}
            x="7"
            y="0"
            width="7"
            height="34"
          />
        </g>
        <g clipPath="url(#arenko-ident-bar-3)">
          <rect
            className={classNames(css.fill, css.fill3)}
            x="15"
            y="0"
            width="7"
            height="34"
          />
        </g>
        <g clipPath="url(#arenko-ident-bar-4)">
          <rect
            className={classNames(css.fill, css.fill4)}
            x="23"
            y="0"
            width="7"
            height="34"
          />
        </g>
        <g clipPath="url(#arenko-ident-bar-5)">
          <rect
            className={classNames(css.fill, css.fill5)}
            x="31"
            y="0"
            width="10"
            height="34"
          />
        </g>
      </g>
    </svg>
  );
};

export default ArenkoIdentSvg;
